// react-native-image-picker for the web, using a file input. On phones,
// launchCamera opens the rear camera; on desktop it falls back to a file
// chooser. Images are scaled down to maxWidth/maxHeight like on mobile, and
// the response has the same { assets: [{ uri, type, fileName, ... }] } shape.
import {registerBlob} from './form-data';

const pick = ({capture, maxWidth, maxHeight, quality = 1}) =>
  new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    if (capture) {
      input.setAttribute('capture', 'environment');
    }
    input.style.display = 'none';

    let settled = false;
    const finish = result => {
      if (settled) {
        return;
      }
      settled = true;
      input.remove();
      resolve(result);
    };
    input.addEventListener('change', async () => {
      const file = input.files?.[0];
      if (!file) {
        return finish({didCancel: true});
      }
      try {
        finish({assets: [await toAsset(file, {maxWidth, maxHeight, quality})]});
      } catch (e) {
        finish({errorCode: 'others', errorMessage: e.message});
      }
    });
    // Fired by current browsers when the chooser is closed without a file.
    input.addEventListener('cancel', () => finish({didCancel: true}));
    document.body.appendChild(input);
    input.click();
  });

const loadImage = url =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not read the selected image'));
    img.src = url;
  });

async function toAsset(file, {maxWidth, maxHeight, quality}) {
  const sourceUrl = URL.createObjectURL(file);
  const img = await loadImage(sourceUrl);
  let {naturalWidth: width, naturalHeight: height} = img;
  const scale = Math.min(
    1,
    (maxWidth || width) / width,
    (maxHeight || height) / height,
  );

  let blob = file;
  let type = file.type || 'image/jpeg';
  if (scale < 1 || quality < 1) {
    width = Math.round(width * scale);
    height = Math.round(height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(img, 0, 0, width, height);
    type = 'image/jpeg';
    blob = await new Promise(r => canvas.toBlob(r, type, quality));
  }
  URL.revokeObjectURL(sourceUrl);

  const uri = URL.createObjectURL(blob);
  const fileName =
    file.name.replace(/\.[^.]+$/, '') + (type === 'image/jpeg' ? '.jpg' : '');
  registerBlob(uri, blob);
  return {uri, type, fileName, fileSize: blob.size, width, height};
}

const withCallback = (promise, callback) => {
  if (callback) {
    promise.then(callback);
  }
  return promise;
};

export const launchCamera = (options = {}, callback) =>
  withCallback(pick({...options, capture: true}), callback);

export const launchImageLibrary = (options = {}, callback) =>
  withCallback(pick(options), callback);
